import { ageWithBabyFormat, beDateToDateObj, DateTimeFormat, distDateFormat, Enum_Patientvisit_Status, formatDate, notNull, useGenerateDescriptiveReportMutation, usePatientProviderViewQuery, useUpdatePatientVisitMutation, getChecksum, usePatientVisitQuery } from "@cyren/common-lib";
import produce from 'immer';
import { Grid, useMediaQuery, useTheme, Button } from "@mui/material";
import { memo, useState, useEffect } from "react";
import { useParams } from "react-router";
import { convVisit } from "../../gql-tools/utils/convert";
import { onError } from '../../utils/apollo-utils';
import classNames from "classnames";
import { H3, H4, H5 } from "../../common/typo/H";
import { parseSex } from "../../utils/models/visit";
import StageProgressMeter from "../../admin/report/components/StageProgressMeter";
import Version from "../../app/Version";
import bgImg from '../../app/assets/Group 10189.svg'
import illChartWriting from '../../assets/ill/chart-writing.svg'
import LoadingAnim from "../../components/LoadingAnim";
import { useCopyToClipboard } from 'usehooks-ts';
import { IoMdCopy } from 'react-icons/io';
import { useToastMsgs } from '../../admin/hooks/use-toast-msgs';
import { ceil, size } from "lodash";
import { FlowLineConfigT } from "../../survey/template/template-types";
import ReportCard from "../../admin/report/ReportCard";
import ReportAdHocCard from "../../admin/report/components/ReportAdHocCard";
import ReactJoyride, { CallBackProps, STATUS } from "react-joyride";
import ModalSendCommunication from "../../admin/report/components/ModalSendCommunication";
import Logo from "../../components/Logo";
import ReportCardForPage from "../../admin/report/ReportCardForPage";
import { ReportStateType } from "../patient-types";
import useTemplateConfigs from "../../survey/template/use-template-configs";
import { toast } from "react-toastify";
import { useLocalization } from "../../report/use-localization";
import { ApolloProvider, ApolloClient, InMemoryCache, from } from '@apollo/client';
import { createUploadLink } from 'apollo-upload-client';
import envs from '../../common/envs';

const steps = [
  {
    target: 'body', // Using 'body' as the target ensures it's not focused on a specific element
    /* eslint-disable */
    content: (
      <div style={{ textAlign: 'left' }}>
        <div className="flex-col gap-2">
          <H3>Welcome to CyrenCare</H3>
          <div>Providers will see a clinical report created from the information provided by the patient.</div>
        </div>
      </div>
    ),
    placement: 'center' as 'center', // Center placement for the introductory message
    /* eslint-enable */
    disableBeacon: true,
    disableArrow: true,
  },
  {
    target: '.joyride-step-clinical-reports',
    /* eslint-disable */
    content: (
      <div style={{ textAlign: 'left' }}>
        <div className="flex-col gap-2">
          <H3>Concise clinical language</H3>
          <div>Patient answers are organized into clinical reports familiar to providers.</div>
        </div>
      </div>
    ),
    placement: 'bottom' as 'bottom',
    disableArrow: true,
    /* eslint-enable */
    disableBeacon: true,
  },
  {
    target: '.joyride-step-custom-questions',
    /* eslint-disable */
    content: (
      <div style={{ textAlign: 'left' }}>
        <div className="flex-col gap-2">
          <H3>Send SMS Messages to Your Patients</H3>
          <div>Ask questions, collect forms, obtain consent, notify and inform your patients by texting them, and get answers from your workstation.</div>
        </div>
      </div>
    ),
    placement: 'bottom' as 'bottom',
    disableArrow: true,
    /* eslint-enable */
    disableBeacon: true
  },
];

// 인증 없는 Apollo Client 생성 (에러 링크 없음)
const noAuthClient = new ApolloClient({
  link: createUploadLink({
    uri: envs.BackendUrl,
    fetchOptions: {
      credentials: 'include',
    },
  }),
  cache: new InMemoryCache(),
  defaultOptions: {
    watchQuery: {
      errorPolicy: 'all',
    },
    query: {
      errorPolicy: 'all',
    },
  },
});

function PaProviderReportInner() {
  const theme = useTheme();
  const isMd = useMediaQuery(theme.breakpoints.down('md'));
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));

  const params = useParams<{
    uuid?: string;
  }>();

  const promptInfoHpi: any[] = [];
  const promptInfoPmh: any[] = [];

  // patientProviderView 데이터를 기반으로 reportState 생성하는 함수
  const generateReportStateFromProviderView = (providerView: any): ReportStateType => {
    const { reports } = providerView;
    const visit = providerView.visit?.data?.attributes;
    
    // systemValueMap 생성
    const systemValueMap: any = {};
    
    // calcedTemplate에서 데이터 추출 (복호화된 데이터)
    if (reports?.calcedTemplate) {
      // CC 데이터 추출
      const ccTemplate = reports.calcedTemplate.find((template: any) => template?.label === "CC");
      if (ccTemplate?.items?.[0]?.reportConfig?.lines?.[0]?.output) {
        const ccText = ccTemplate.items[0].reportConfig.lines[0].output
          .replace(/<cyrenvalue>/g, '')
          .replace(/<\/cyrenvalue>/g, '')
          .trim();
        if (ccText) {
          systemValueMap.cc = ccText;
        }
      }
      
      // 환자 정보 추출 (Patient info 템플릿에서)
      const patientInfoTemplate = reports.calcedTemplate.find((template: any) => template?.label === "Patient info");
      if (patientInfoTemplate?.items?.[0]?.reportConfig?.lines) {
        patientInfoTemplate.items[0].reportConfig.lines.forEach((line: any) => {
          if (line.items) {
            line.items.forEach((item: any) => {
              if (item.questionKey && item.output) {
                const value = item.output
                  .replace(/<cyrenvalue>/g, '')
                  .replace(/<\/cyrenvalue>/g, '')
                  .trim();
                
                if (value) {
                  switch (item.questionKey) {
                    case 'q-patient-first-name':
                      systemValueMap.firstName = value;
                      break;
                    case 'q-patient-last-name':
                      systemValueMap.lastName = value;
                      break;
                    case 'q-patient-sex':
                      systemValueMap.sex = value;
                      break;
                    case 'q-patient-birthday':
                      systemValueMap.dateOfBirth = value;
                      break;
                  }
                }
              }
            });
          }
        });
      }
    }
    
    // fallback: visit에서 환자 정보 추출 (암호화된 데이터일 수 있음)
    if (visit && Object.keys(systemValueMap).length === 0) {
      if (visit.firstName) systemValueMap.firstName = visit.firstName;
      if (visit.lastName) systemValueMap.lastName = visit.lastName;
      if (visit.sex) systemValueMap.sex = visit.sex;
      if (visit.dob) systemValueMap.dateOfBirth = visit.dob;
    }
    
    // treeStates 생성 (reports.treeStates가 있다면 사용, 없다면 빈 배열)
    const treeStates = reports?.treeStates || [];
    
    // globalAnswerMap 생성 (treeStates에서 추출)
    const globalAnswerMap: any = {};
    treeStates.forEach((treeState: any) => {
      if (treeState.answerMap) {
        Object.assign(globalAnswerMap, treeState.answerMap);
      }
    });
    
    // calcedTemplate에서 추가 데이터 추출하여 systemValueMap 보완
    if (reports?.calcedTemplate) {
      reports.calcedTemplate.forEach((template: any) => {
        if (template?.items?.[0]?.reportConfig?.lines) {
          template.items[0].reportConfig.lines.forEach((line: any) => {
            if (line.items) {
              line.items.forEach((item: any) => {
                if (item.questionKey && item.output) {
                  const value = item.output
                    .replace(/<cyrenvalue>/g, '')
                    .replace(/<\/cyrenvalue>/g, '')
                    .trim();
                  
                  if (value) {
                    // questionKey를 기반으로 systemValueMap에 추가
                    const key = item.questionKey.replace('q-', '');
                    if (!systemValueMap[key]) {
                      systemValueMap[key] = value;
                    }
                  }
                }
              });
            }
          });
        }
      });
    }
    
    return {
      locale: "english",
      isDemo: false,
      globalAnswerMap,
      systemValueMap,
      treeStates,
      nextTreeKeys: [],
      presentIllness: [],
      ccKey: undefined,
      descriptiveReport: undefined,
      errorState: {
        unhandledErrors: [],
        maxErrorsReached: false
      },
      intelligentSymptoms: reports?.intelligentSymptoms || undefined,
    };
  };

  // useTemplateConfigs 대신 직접 reportState 관리
  const [reportState, setReportState] = useState<ReportStateType | null>(null);
  const [loadingSurveyTemplate, setLoadingSurveyTemplate] = useState(false);

  // 번역 기능 추가
  const [{ appStateCookie }, { td, trans }] = useLocalization();
  
  // 디버깅을 위한 로그 추가
  console.log('PaProviderReport - td function:', td);
  console.log('PaProviderReport - appStateCookie:', appStateCookie);
  
  // 올바른 키로 테스트
  console.log('PaProviderReport - o-physicalactivity:', td('o-physicalactivity'));
  console.log('PaProviderReport - o-walking:', td('o-walking'));
  console.log('PaProviderReport - o-exercise:', td('o-exercise')); // 이 키는 존재하지 않음

  const handleReportGenerated = (generatedText: string | null) => {
    // reportState를 업데이트하는 로직이 핵심입니다.
    console.log('generatedText', generatedText);
    if (generatedText && reportState) {
      // generatedReportText 상태도 직접 업데이트
      setGeneratedReportText(generatedText);
      
      const newState = produce(reportState, (dr: any) => {
        dr.descriptiveReport = generatedText; // <-- 이 부분이 reportState를 업데이트합니다.
      });
      setReportState(newState); // <-- 업데이트된 newState로 reportState를 설정합니다.
    } else {
      // null인 경우도 처리
      setGeneratedReportText(null);
    }
  };

  const [openSendCommunication, setOpenSendCommunication] = useState<boolean>(false);
  const [runTour, setRunTour] = useState(true);
  const [stepIndex, setStepIndex] = useState(0);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedReportText, setGeneratedReportText] = useState<string | null>(null);
  const [, copy] = useCopyToClipboard();
  const { showCopiedMsg } = useToastMsgs();
  const [generateDescriptiveReport, { data: reportData, loading: loadingReport, error: reportError }] = useGenerateDescriptiveReportMutation({
      onError,
    });
  const [updateVisit] = useUpdatePatientVisitMutation({
    onError,
  });

  const { data, loading, error } = usePatientProviderViewQuery({
    fetchPolicy: 'cache-and-network',
    skip: !params.uuid,
    variables: {
      uuid: params.uuid!,
    },
    onError: (err) => {
      console.error('PaProviderReport - GraphQL Error:', err);
      console.error('PaProviderReport - Error details:', err.message, err.graphQLErrors, err.networkError);
      onError(err);
    },
    onCompleted: (d) => {
      console.log('PaProviderReport - Query completed successfully:', d);
      // patientProviderView에서 받은 데이터로 reportState 생성
      if (d?.patientProviderView?.reports) {
        const generatedReportState = generateReportStateFromProviderView(d.patientProviderView);
        setReportState(generatedReportState);
      }
    },
  });

  console.log('PaProviderReport - data:', data);
  console.log('PaProviderReport - loading:', loading);
  console.log('PaProviderReport - params.uuid:', params.uuid);
  
  // 데이터가 로드되었을 때 상세 정보 출력
  if (data) {
    console.log('PaProviderReport - visit data:', data.patientProviderView?.visit?.data);
    console.log('PaProviderReport - reports data:', data.patientProviderView?.reports);
    console.log('PaProviderReport - treeStates:', data.patientProviderView?.reports?.treeStates);
    
    // treeStates에서 o- 키들을 찾아서 번역 테스트
    const treeStates = data.patientProviderView?.reports?.treeStates || [];
    const oKeys = new Set<string>();
    
    treeStates.forEach((treeState: any) => {
      const { answerMap } = treeState;
      if (answerMap) {
        Object.entries(answerMap).forEach(([questionKey, answerValue]: [string, any]) => {
          if (answerValue.answerKeys) {
            answerValue.answerKeys.forEach((key: string) => {
              if (key.startsWith('o-')) {
                oKeys.add(key);
              }
            });
          }
        });
      }
    });
    
    console.log('PaProviderReport - found o- keys:', Array.from(oKeys));
    
    // 각 o- 키에 대해 번역 테스트
    oKeys.forEach(key => {
      console.log(`PaProviderReport - ${key}:`, td(key));
    });
  }

  // treeStates의 answerMap을 읽어서 배열 생성
  const treeStatesInfo: string[] = [];
  
  // visit에서 treeStates 데이터를 가져옵니다
  const treeStates = data?.patientProviderView?.reports?.treeStates || [];
  
  treeStates.forEach((treeState: any) => {
    const { treeKey: stateTreeKey, answerMap } = treeState;
    
    if (answerMap && Object.keys(answerMap).length > 0) {
      Object.entries(answerMap).forEach(([key, value]: [string, any]) => {
        if (value.answerValues && !stateTreeKey.includes("sx-") && !stateTreeKey.includes("chief-complaint")) {
          if (value.answerValues.length > 0) {
            treeStatesInfo.push(`${stateTreeKey}, ${key.replace("q-", "")}: ${value.answerValues.join(',')}`);
          } else {
            treeStatesInfo.push(`${stateTreeKey}, ${key.replace("q-", "")}: None`);
          }
        }
      });
    }
  });

  const handleJoyrideCallback = (joyrideData: CallBackProps) => {
    const { action, index, status, type } = joyrideData;
    if (type === 'step:after') {

      if (steps[index + 1]?.target === '.joyride-step-custom-questions') {
        setRunTour(false);
        setOpenSendCommunication(true);

        setTimeout(() => {
          setStepIndex(index + 1);
          setRunTour(true); // Resume the tour after a delay
        }, 250);
      } else if (action === 'prev') {
        setStepIndex(index - 1);
      } else {
        setStepIndex(index + 1);
      }
    }

    // @ts-ignore
    if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
      // Tour finished or skipped
      setRunTour(false);
      setOpenSendCommunication(false);
    }
    console.log(joyrideData); //eslint-disable-line no-console
  };

  const visit = convVisit(data?.patientProviderView?.visit?.data);

  useEffect(() => {
    if (reportState) {
      setGeneratedReportText(reportState?.descriptiveReport ?? null);
    }
  }, [reportState?.descriptiveReport]);

  if (loading) {
    return (
      <div className='flex-1 h-full overflow-hidden flex-center'>
        <LoadingAnim />
      </div>
    );
  }

  if (error) {
    console.error('PaProviderReport - Rendering error state:', error);
    return (
      <div className='flex-1 h-full overflow-hidden flex-center flex-col gap-4'>
        <div className='text-red-500 text-lg font-bold'>Error loading provider report</div>
        <div className='text-gray-600'>Error: {error.message}</div>
        {error.graphQLErrors?.map((gqlError, index) => (
          <div key={index} className='text-red-400 text-sm'>
            GraphQL Error: {gqlError.message}
          </div>
        ))}
        {error.networkError && (
          <div className='text-red-400 text-sm'>
            Network Error: {error.networkError.message}
          </div>
        )}
        <div className='text-gray-500 text-xs'>
          UUID: {params.uuid}
        </div>
      </div>
    );
  }

  if (!reportState && !loading) {
    return (
      <div className='flex-1 h-full overflow-hidden flex-center flex-col gap-4'>
        <div className='text-gray-600'>No data available</div>
        <div className='text-gray-500 text-xs'>
          UUID: {params.uuid}
        </div>
      </div>
    );
  }

  if (!reportState) {
    return (
      <div className='flex-1 h-full overflow-hidden flex-center'>
        <LoadingAnim />
      </div>
    );
  }

  const reporting = visit?.status == null || visit?.status === Enum_Patientvisit_Status.Reporting;
  const localeName = reportState?.locale || 'english';

  // not all forms will collect a phone, and not all patients will provide one.
  // however, if phone is provided and verfied, enable phone-related functionality
  const patientPhone = visit?.verifiedPhone;
  // demo functionality - if a question with the key 'q-signature' is found, render it at the bottom of the page
  const signature = reportState?.treeStates
    ?.map((ts: any) => {
      return ts?.answerMap?.['q-signature']?.answerValues?.[0];
    })
    .filter(notNull)?.[0];

  const firstVisitValue = reportState?.treeStates
    ?.map((ts: any) => {
      return ts?.answerMap?.['q-pmhx-first-existing']?.answerKeys?.[0];
    })
    .filter(notNull)?.[0];
  const isFirstVisit = firstVisitValue === 'o-yes';

  const losDate = beDateToDateObj(visit?.createdAt);

  const calcedTemplate = data?.patientProviderView?.reports?.calcedTemplate as FlowLineConfigT[];
  const templatesToShow = calcedTemplate?.filter(i => i?.foundTreeState || i?.items.find(j => j.type === 'rep-phi-list'));

  const lineLen = size(templatesToShow);
  const lines1 = templatesToShow?.slice(0, ceil(lineLen / 2)) || [];
  const lines2 = templatesToShow?.slice(ceil(lineLen / 2)) || [];
  const phis = data?.patientProviderView?.reports?.phis || [];

  console.log('data?.patientProviderView?.reports', data?.patientProviderView?.reports);

  phis.forEach((phi: any) => {
    const cleanedOutput = phi.lineOutput.replace(/<cyrenvalue>/g, "").replace(/<\/cyrenvalue>/g, "");
    promptInfoHpi.push(`${phi.treeKey}: ${cleanedOutput} Onset Date: ${phi.onsetDate} Relative Onset: ${phi.relativeOnset ?? ""}`);
    if (phi.sublinesOutput.length > 0) {
      phi.sublinesOutput.forEach((subline: any) => {
        promptInfoHpi.push(`${phi.treeKey}: ${subline} Onset Date: ${phi.onsetDate} Relative Onset: ${phi.relativeOnset ?? ""}`);
      });
    }
  });

  

  return (

    <div
      id='cyren-admin'
      className={classNames(' admin-v2 flex flex-1 flex-col h-screen')}
    >
      {/* {visit &&
        <ReactJoyride
          steps={steps}
          stepIndex={stepIndex}
          continuous
          showProgress
          showSkipButton
          run={runTour}

          
          hideCloseButton
          disableOverlayClose
          scrollOffset={250}
          disableOverlay
          callback={handleJoyrideCallback}

          styles={{
            options: {
              arrowColor: '#8255FF',
              backgroundColor: '#8255FF',
              primaryColor: '#000',
              textColor: '#ffffff',
              width: 450,
              zIndex: 1600,
            },
            tooltip: {
              margin: '0 20px',
              maxWidth: 'calc(100% - 40px)',
            },
            overlay: {
              zIndex: 1500,
            },
            buttonBack: {
              color: '#ffffff'
            }
          }}
        />
      } */}
      {openSendCommunication && (
        <ModalSendCommunication
          onClose={() => {
            setOpenSendCommunication(false);
          }}
          visit={visit!}
        />
      )}

      {/* CONTENTS */}
      <div className='flex-col flex-1 bg-base-300'>
        <div
          className={classNames('h-[7.5rem]')}
          style={{
            backgroundImage: `url("${bgImg}")`,
          }}
        >
          <div className="p-4 flex-row items-center h-full">
            <Logo forDoctor className="w-[4rem]" />
          </div>
        </div>
        <div
          className={classNames(
            'flex flex-1 w-full',
          )}
        >
          <div
            className={classNames(
              'flex pt-8 md:px-10 flex-1'
            )}
          >

            <div className={classNames('flex-1 flex-col gap-3')}>

              <div className='flex-col'>

                <Grid container spacing={3} className={classNames(isMd && "bg-white p-3", "joyride-step-patient-basics")}>
                  <Grid item xs={12} sm={12} md={4} lg={4}
                  >
                    {visit?.firstName && (
                      <div className='flex items-end gap-2 font-bold'>
                        <H4 className='text-[1.5rem] font-extrabold'>
                          {visit?.firstName} {visit?.lastName}
                        </H4>
                        <span className='text-sm pb-[0.12rem]'>
                          {parseSex({ sexStr: visit?.sex })} /
                          {ageWithBabyFormat(visit.dob, reportState?.systemValueMap?.age)}
                        </span>
                      </div>
                    )}

                    {localeName && (
                      <div className='text-sm font-semibold capitalize opacity-60'>{localeName}</div>
                    )}

                    {visit && <div className="pt-2 pb-2"><StageProgressMeter visit={visit} /></div>}

                  </Grid>

                  <Grid item xs={12} sm={12} md={4} lg={4} className="">
                    {reporting && (
                      <>
                        <div className='flex-center'>
                          <img className="max-h-20" src={illChartWriting} />
                        </div>
                        <div className='flex-center'>Patient is writing the report...</div>
                      </>
                    )}
                  </Grid>

                  <Grid item xs={12} sm={12} md={4} lg={4}
                    className={classNames("flex-none flex-col",
                      isMd && "pt-3",
                      isMdUp && "justify-end")}>
                    <div className={classNames(
                      'flex-col flex-none gap-2 text-sm opacity-80',
                      isMd && "w-full border-t",
                      isMdUp && 'border-transparent pt-0 text-end')}>
                      {firstVisitValue ? (
                        <div>{isFirstVisit ? 'New patient' : 'Returning patient'}</div>
                      ) : (
                        <div>-</div>
                      )}
                      <div>
                        {formatDate(losDate, {
                          formatStr: DateTimeFormat,
                        })}
                      </div>
                      {/* <div>08/20/23 10:47</div> */}
                      <div>
                        {'LOS: '}
                        {distDateFormat(losDate)}
                      </div>
                    </div>
                  </Grid>
                </Grid>
              </div>
              <div className={classNames("flex-col flex-1 gap-5", "joyride-step-clinical-reports")}>
                <div className='grid flex-1 grid-cols-1 gap-2 md:gap-5 md:grid-cols-2'>
                  <div className='flex-col flex-1 gap-2 md:gap-5'>
                    {lines1.map((line, idx) => {
                      const hasDownloadableForm = line.items?.[0]?.reportConfig?.treeKey?.endsWith('consent')
                        ? line?.output : null;
                      const demoSignature = hasDownloadableForm && signature;
                      const key = `${idx}-${line.id}`;
                      const joyrrideClassname = `joyride-step-${line?.label?.replace(' ', '-').toLowerCase()}`
                      return (
                        <div className={joyrrideClassname}>
                            <ReportCardForPage key={key} keyId={key} line={line} phis={phis} isAddOnForm={false} demoSignature={demoSignature} visit={visit} onReportGenerated={handleReportGenerated} setIsGeneratingDesc={setIsGenerating} isDemo reportState={reportState!} td={td}/>
                        </div>
                      );
                    })}
                  </div>
                  <div className='flex-col flex-1 gap-2 md:gap-5'>
                    {lines2.map((line, idx) => {
                      const hasDownloadableForm = line.items?.[0]?.reportConfig?.treeKey?.endsWith('consent')
                        ? line?.output : null;
                      const demoSignature = hasDownloadableForm && signature;
                      const key = `${idx}-${line.id}`;
                      const joyrrideClassname = `joyride-step-${line?.label?.replace(' ', '-').toLowerCase()}`
                      return (
                        <div className={joyrrideClassname}>
                          <ReportCardForPage key={key} keyId={key} line={line} phis={phis} isAddOnForm={false} demoSignature={demoSignature} visit={visit} onReportGenerated={handleReportGenerated} setIsGeneratingDesc={setIsGenerating} isDemo reportState={reportState!} td={td}/>
                        </div>
                      );
                    })}
                    {patientPhone &&
                      <ReportAdHocCard
                        adHocData={visit?.adHocState}
                        patientMessages={[] /*patientMessages*/}
                        locale={reportState?.locale}
                      />
                    }
                  </div>
                </div>


                <div className='h-24 pb-10'></div>
                <div className='h-24 pb-10'></div>
              </div>

            </div>
          </div>
        </div>
      </div>
      <div className={classNames('block md:hidden')}><Version /></div>
    </div>

  );
}

function PaProviderReport() {
  return (
    <ApolloProvider client={noAuthClient}>
      <PaProviderReportInner />
    </ApolloProvider>
  );
}

export default memo(PaProviderReport);
